import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { useAuth } from "@/components/AuthProvider";
import { authLog } from "@/lib/auth-log";
import { apiFetch } from "@/lib/api";
import {
  HUB_GOOGLE_WORKSPACE_EMAIL_SUFFIX,
  isExternalHubEmailForMagicLink,
  isHubGoogleWorkspaceEmail,
} from "@/lib/hubSignInEmail";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { devAuthOAuthRedirectTo } from "@/lib/devPrimaryOrigin";
import { getOAuthErrorFromQuery, isAwaitingSupabaseUrlSession } from "@/lib/oauth-url";
import { safeReturnTo } from "@/lib/safeReturnTo";

export function SignIn() {
  const { session, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = safeReturnTo(new URLSearchParams(location.search).get("returnTo"));
  const urlOAuthError = getOAuthErrorFromQuery(location.search);
  const awaitingUrlSession =
    session === null && isAwaitingSupabaseUrlSession(location.search, location.hash);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const loggedOauthPath = useRef(false);
  if (awaitingUrlSession && !loggedOauthPath.current) {
    loggedOauthPath.current = true;
    authLog("signIn:oauthParamsInPath", { message: "waiting for Supabase to process" });
  }
  if (!awaitingUrlSession) {
    loggedOauthPath.current = false;
  }

  const workspaceEmailEntered = isHubGoogleWorkspaceEmail(email);
  const externalHubEmailEntered = isExternalHubEmailForMagicLink(email);

  useEffect(() => {
    if (!loading && session) {
      const destination = returnTo ?? "/";
      authLog("signIn:navigate:afterSession", {
        userId: session.user.id,
        email: session.user.email,
        destination,
      });
      navigate(destination, { replace: true });
    }
  }, [session, loading, navigate, returnTo]);

  if (urlOAuthError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-amber-950">Sign-in did not complete</h1>
          <p className="mt-1 font-mono text-sm text-amber-900">{urlOAuthError.error}</p>
          {urlOAuthError.errorDescription ? (
            <p className="mt-2 text-sm text-amber-900/90">{urlOAuthError.errorDescription}</p>
          ) : null}
        </div>
      </div>
    );
  }
  if ((awaitingUrlSession || loading) && !session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">Signing you in…</h1>
          <p className="mt-2 text-sm text-slate-600">
            Completing sign-in. This page will redirect when your session is ready.
          </p>
        </div>
      </div>
    );
  }

  const handleGoogleSignIn = async (): Promise<void> => {
    if (!supabase) {
      setError(
        "Supabase is not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to `ui/.env` (or keep them in the repository root and mirror into `ui/.env` for Vite), then restart the dev server.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    const oauthRedirectTo = devAuthOAuthRedirectTo(returnTo ?? "/");
    authLog("signIn:oauth:redirect", { provider: "google", redirectTo: oauthRedirectTo });
    const { data: oauthData, error: authError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: oauthRedirectTo,
        queryParams: {
          hd: "smartgridsolutions.com",
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (authError) {
      authLog("signIn:oauth:error", { message: authError.message });
      setError(authError.message);
      setBusy(false);
      return;
    }
    if (oauthData?.url) {
      authLog("signIn:oauth:leaving", { toProvider: "google" });
    }
  };

  const handleEmailLink = async (): Promise<void> => {
    if (!externalHubEmailEntered) {
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await apiFetch<{ ok: boolean; emailSent: boolean }>("/api/auth/magic-link", {
        method: "POST",
        body: { email: email.trim() },
      });
      setMessage("If that email is registered for the hub, a sign-in link is on the way.");
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "Could not send sign-in link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign in to the SGS Hub</CardTitle>
        </CardHeader>
        <CardBody>
          {!isSupabaseConfigured ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <p className="font-medium">Supabase environment variables are missing.</p>
              <p className="mt-1 text-amber-800">
                The dev client is wired from the same project{" "}
                <code className="rounded bg-amber-100 px-1">.env</code> as the integrator: add{" "}
                <code className="rounded bg-amber-100 px-1">SUPABASE_URL</code> and{" "}
                <code className="rounded bg-amber-100 px-1">SUPABASE_PUBLISHABLE_KEY</code> (publishable
                key from Supabase → Project Settings → API, not the service role).{" "}
                <code className="rounded bg-amber-100 px-1">ui/vite.config.ts</code> maps those (and
                optional <code className="rounded bg-amber-100 px-1">VITE_*</code> overrides in{" "}
                <code className="rounded bg-amber-100 px-1">ui/.env</code>) into the browser bundle.
              </p>
            </div>
          ) : null}
          <p className="text-sm text-slate-600">
            Smart Grid Solutions staff on Google Workspace: sign in with your
            <span className="font-medium text-slate-900"> {HUB_GOOGLE_WORKSPACE_EMAIL_SUFFIX} </span>
            account below.
          </p>
          {error ? (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}
          <Button
            className="mt-5 w-full"
            onClick={() => void handleGoogleSignIn()}
            isLoading={busy}
            disabled={!isSupabaseConfigured}
          >
            Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wide text-slate-400">
            <span className="h-px flex-1 bg-slate-200" />
            other hub account
            <span className="h-px flex-1 bg-slate-200" />
          </div>
          <p className="text-sm text-slate-600">
            Hub users outside Google Workspace ({HUB_GOOGLE_WORKSPACE_EMAIL_SUFFIX}) can request a
            one-time email link.
          </p>
          <Label className="mt-3 text-sm" htmlFor="hub-email-link">
            Work email
          </Label>
          <Input
            id="hub-email-link"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setMessage(null);
            }}
            className="mt-1 shadow-sm"
            placeholder="name@example.com"
          />
          {workspaceEmailEntered ? (
            <p className="mt-2 text-sm text-slate-600">
              {HUB_GOOGLE_WORKSPACE_EMAIL_SUFFIX} accounts sign in with{" "}
              <span className="font-medium text-slate-900">Continue with Google</span> above.
            </p>
          ) : null}
          {externalHubEmailEntered ? (
            <Button
              variant="secondary"
              className="mt-3 w-full"
              onClick={() => void handleEmailLink()}
              isLoading={busy}
              disabled={!isSupabaseConfigured}
            >
              Send email link
            </Button>
          ) : null}
          {message ? <p className="mt-3 text-sm text-slate-600">{message}</p> : null}
        </CardBody>
      </Card>
    </div>
  );
}
